// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { RegexPatterns } from '@/api/constants';
import type { PasswordPolicyGetResponse, PasswordPolicyUpdateRequest } from '@/api/types';

/**
 * Maps the API password policy response to the update request format.
 * Extracts the `.value` properties from each setting.
 *
 * @param response - The password policy response from the API
 * @returns A simplified password policy object with just the values
 * const policy = mapPasswordPolicyResponse(apiResponse);
 * // policy = { minimumLength: 8, requireAlphabetic: true, ... }
 */
export const mapPasswordPolicyResponse = (
  response: PasswordPolicyGetResponse,
): PasswordPolicyUpdateRequest => ({
  minimumLength: response.minimumLength.value,
  maximumLength: response.maximumLength.value,
  requireAlphabetic: response.requireAlphabetic.value,
  requireNumeric: response.requireNumeric.value,
  requireSpecial: response.requireSpecial.value,
  requireLowercase: response.requireLowercase.value,
  requireUppercase: response.requireUppercase.value,
  passwordReuse: response.passwordReuse.value,
  passwordExpiration: response.passwordExpiration.value,
});

/**
 * Builds a regex pattern for password strength validation based on the provided password policy.
 *
 * @param policy - The password policy containing requirements for the password
 * @returns A RegExp that validates passwords against the policy requirements
 * const regex = buildPasswordStrengthRegex(policy);
 * regex.test('MyPass123'); // returns true or false
 */
export const buildPasswordStrengthRegex = (policy: PasswordPolicyUpdateRequest): RegExp => {
  const patterns: string[] = [RegexPatterns.STRING_START];

  if (policy.requireAlphabetic) {
    patterns.push(RegexPatterns.REQUIRE_ALPHABETIC);
  }
  if (policy.requireLowercase) {
    patterns.push(RegexPatterns.REQUIRE_LOWERCASE);
  }
  if (policy.requireUppercase) {
    patterns.push(RegexPatterns.REQUIRE_UPPERCASE);
  }
  if (policy.requireNumeric) {
    patterns.push(RegexPatterns.REQUIRE_NUMERIC);
  }
  if (policy.requireSpecial) {
    patterns.push(RegexPatterns.REQUIRE_SPECIAL);
  }

  patterns.push(`[\\x20-\\x7E]{${policy.minimumLength},${policy.maximumLength}}`);
  patterns.push(RegexPatterns.STRING_END);

  return new RegExp(patterns.join(''));
};
